/**
 * Platform detection utilities for Beacio
 */

// SB-SDK-12: the install-state marker derivation now lives in the single shared
// install-state module (consumed here, by the react-sdk ExtensionDetector, and by
// the headless API), so the detection logic is shared rather than duplicated.
import { getInstallState, type ExtensionInstallState } from './install-state';

export type { ExtensionInstallState } from './install-state';

/**
 * Is this an iOS/iPadOS DEVICE, irrespective of which browser engine surface is
 * rendering the page? Extracted from {@link isIOSSafari} because GH #448 needs the
 * device test WITHOUT the `Safari/`-token test: a Home Screen web app runs on iOS
 * but reports neither `Safari/` nor `Version/`. iPadOS presents a desktop
 * "Macintosh" UA, so the `MacIntel` + touch-points pair is the arm that catches it.
 *
 * `nav` is a REQUIRED parameter (owner's no-optional-args rule) — the caller owns
 * the `typeof navigator === 'undefined'` guard, and passing the global explicitly
 * keeps the predicate pure and testable without mutating jsdom.
 */
export function isIOSDevice(nav: Navigator): boolean {
  const ua = nav.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
}

export function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;

  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

  return isIOSDevice(navigator) && isSafari;
}

/**
 * GH #448: is this page running as an iOS Home Screen web app ("Add to Home
 * Screen" with "Open as Web App" ON)? Safari extensions NEVER load in that
 * standalone context, so beacio is inert there even when the app is installed and
 * enabled — and because the standalone UA drops the `Safari/` token,
 * {@link isIOSSafari} is false and every caller would otherwise report the generic
 * "unsupported browser" / "not installed" dead end instead of the real diagnosis
 * ("open this page in Safari, or re-add it with Open as Web App off").
 *
 * Two signals, either of which is sufficient: the classic WebKit
 * `navigator.standalone` boolean, and the `(display-mode: standalone)` media query
 * (which also covers third-party-browser Home Screen web apps on iOS 16.4+, where
 * extensions are likewise unavailable — the diagnosis holds). This is a
 * DETERMINISTIC platform API, not a heuristic like the Private-Browsing probe.
 *
 * `nav` and `win` are REQUIRED parameters: the caller owns the
 * `typeof window !== 'undefined'` guard. The `typeof win.matchMedia` test is not an
 * optionality check on our own API — it is a capability probe for environments
 * (jsdom, older WebKit) that do not implement matchMedia at all.
 */
export function isIOSHomeScreenWebApp(nav: Navigator, win: Window): boolean {
  return (
    isIOSDevice(nav) &&
    ((nav as Navigator & { standalone?: boolean }).standalone === true ||
      (typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches))
  );
}

export async function getExtensionInstallState(): Promise<ExtensionInstallState> {
  // Fast-path: use core's platform detection (detect now lives INSIDE @beacio/core,
  // so this is an intra-package import — no optional-peer boundary any more).
  try {
    const { detectPlatform } = await import('../platform');
    if (detectPlatform() === 'safari-extension') return 'active';
  } catch { /* defensive — platform probe must never throw the install flow */ }

  return new Promise((resolve) => {
    // Method 1: Check for the global marker set by injected-full.ts
    const immediateState = getInstallState();
    if (immediateState !== 'not-installed') {
      resolve(immediateState);
      return;
    }

    // Method 3: Wait briefly for injection to complete
    // The content script runs at document_start, so injection should be fast
    let checks = 0;
    const interval = setInterval(() => {
      checks++;
      const state = getInstallState();
      if (state !== 'not-installed') {
        clearInterval(interval);
        resolve(state);
      }
      if (checks > 20) {
        // 2 seconds max wait
        clearInterval(interval);
        resolve('not-installed');
      }
    }, 100);
  });
}

export async function isExtensionInstalled(): Promise<boolean> {
  return (await getExtensionInstallState()) !== 'not-installed';
}
